import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";
import {
  AccountManager,
  AccountManagerFormInput,
  ApiResponse,
  ManageEntitiesRequestPayload,
} from "../../types/tenants";
import { Toast } from "primereact/toast";

export const useAccountManager = () => {
  const { managerId } = useParams();
  const toastRef = useRef<Toast>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [accountManagerInfo, setAccountManagerInfo] = useState<AccountManager>({
    id: "",
    given_name: "",
    other_name: "",
    surname: "",
    preferred_name: "",
    country: "",
    email: "",
    phone_code: "",
    phone_number: "",
  });
  const handleSave = async (data: AccountManagerFormInput) => {
    try {
      if (managerId) {
        setIsLoading(true);
        const payload = {
          ...data,
          country: data.country?.code || "",
        };
        const response: ApiResponse = await apiService(
          API_ROUTES_V2.UPDATE_ACCOUNT_MANAGER_DETAILS,
          "PATCH",
          {
            suppressToast: true,
            replace: { id: managerId },
            body: payload,
          },
        );
        setIsEditing(false);
        if (toastRef.current) {
          toastRef.current.show({
            severity: "success",
            summary: "Personal Information",
            detail: "has been updated.",
            icon: "pi pi-check",
          });
        }
        setAccountManagerInfo(response.data);
        return response;
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Something went wrong";
      toastRef.current?.show({
        severity: "error",
        summary: `Error. ${message}`,
        icon: "none",
      });
      throw new Error("Failed to fetch account manager detail");
    } finally {
      setIsLoading(false);
    }
  };
  const handleFieldChange = (fieldName: string, value: string) => {
    setAccountManagerInfo((prev) => ({ ...prev, [fieldName]: value }));
  };

  const fetchAccountManagerDetails = async () => {
    try {
      if (managerId) {
        setIsDetailsLoading(true);
        const response: ApiResponse = await apiService(
          API_ROUTES_V2.FETCH_ACCOUNT_MANAGER_DETAILS,
          "GET",
          {
            suppressToast: true,
            replace: { id: managerId },
          },
        );
        setAccountManagerInfo(response.data);
        return response;
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Something went wrong";
      toastRef.current?.show({
        severity: "error",
        summary: `Error. ${message}`,
        icon: "none",
      });
      throw new Error("Failed to fetch account manager detail");
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const fetchManagedEntities = async (
    payload: ManageEntitiesRequestPayload,
  ) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.FETCH_MANAGED_ENTIIES,
        "GET",
        {
          suppressToast: true,
          replace: {
            account_manager_id: payload.account_manager_id,
            sortBy: payload.sortBy,
            sortOrder: payload.sortOrder,
            page: payload.page,
            pageSize: payload.pageSize,
          },
        },
      );
      return response;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Something went wrong";
      toastRef.current?.show({
        severity: "error",
        summary: `Error. ${message}`,
        icon: "none",
      });
      throw new Error("Failed to fetch managed entities");
    }
  };
  return {
    handleSave,
    accountManagerInfo,
    setAccountManagerInfo,
    handleFieldChange,
    fetchAccountManagerDetails,
    toastRef,
    isEditing,
    setIsEditing,
    isLoading,
    isDetailsLoading,
    fetchManagedEntities,
  };
};
